import { AlertTriangle } from "lucide-react";
import { useState } from "react";
import { useToast } from "../../context/ToastContext";
import { useAsync } from "../../hooks/useAsync";
import { apiRequest } from "../../lib/api";
import type { MailboxRecord } from "../../lib/types";

interface PortalMailboxResponse {
  settings: { mailboxId: string | null };
}

export function PortalMailboxSettingsPanel({ mailboxes }: { mailboxes: MailboxRecord[] }) {
  const { showToast } = useToast();
  const [draftMailboxId, setDraftMailboxId] = useState<string>();
  const [busy, setBusy] = useState(false);
  const result = useAsync((signal) => apiRequest<PortalMailboxResponse>("/admin/portal-mailbox", { signal }), [mailboxes], { keepPreviousData: true });
  const configuredMailboxId = result.data?.settings.mailboxId || "";
  const selectedMailboxId = draftMailboxId ?? configuredMailboxId;
  const activeMailboxes = mailboxes.filter((mailbox) => mailbox.status === "active");
  const configuredUnavailable = Boolean(configuredMailboxId && !activeMailboxes.some((mailbox) => mailbox.id === configuredMailboxId));
  const selectedUnavailable = Boolean(selectedMailboxId && !activeMailboxes.some((mailbox) => mailbox.id === selectedMailboxId));
  const selectedMailbox = mailboxes.find((mailbox) => mailbox.id === selectedMailboxId);

  async function save() {
    setBusy(true);
    try {
      const saved = await apiRequest<PortalMailboxResponse>("/admin/portal-mailbox", {
        method: "PUT",
        body: { mailboxId: selectedMailboxId || null },
      });
      result.setData(saved);
      setDraftMailboxId(undefined);
      showToast("Portal mailbox saved.", "success");
      await result.refresh();
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Could not save the portal mailbox.", "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="settings-section card">
      <header><div><h2>Customer portal mailbox</h2><p>Choose the mailbox for new tickets submitted through the customer portal.</p></div></header>
      <div className="inline-admin-form">
        {result.error && <p role="alert">{result.error} <button className="button button--quiet button--small" type="button" disabled={busy || result.refreshing} onClick={() => void result.refresh()}>Retry</button></p>}
        {result.loading && !result.data ? <p role="status">Loading portal mailbox...</p> : result.data && <>
          <label className="field">
            <span>Default mailbox for portal tickets</span>
            <select value={selectedMailboxId} disabled={busy} aria-describedby="portal-mailbox-help" onChange={(event) => setDraftMailboxId(event.target.value)}>
              <option value="">Automatic (existing routing)</option>
              {selectedUnavailable && <option value={selectedMailboxId} disabled>{selectedMailbox ? `${selectedMailbox.name} (${selectedMailbox.address}) - disabled` : "Unavailable mailbox"}</option>}
              {activeMailboxes.map((mailbox) => <option key={mailbox.id} value={mailbox.id}>{mailbox.name} ({mailbox.address})</option>)}
            </select>
            <small id="portal-mailbox-help">Automatic uses the oldest active mailbox with an address beginning with support@, or the oldest active mailbox if none matches.</small>
          </label>
          <p className="muted-copy">This applies only to new customer portal tickets. Existing tickets, incoming email, and tickets created by agents or integrations keep their current routing.</p>
          {configuredUnavailable && <div className="settings-callout" role="alert"><AlertTriangle aria-hidden="true" /><p><strong>The saved portal mailbox is unavailable.</strong>New portal tickets use automatic routing until that mailbox is active again or you save another selection. Select Automatic to clear this setting.</p></div>}
          {selectedUnavailable && !configuredUnavailable && <p role="alert">The selected mailbox is no longer active. Choose an active mailbox or Automatic before saving.</p>}
          {activeMailboxes.length === 0 && <p role="alert">No active mailboxes are available. Activate a mailbox so new portal tickets can be created.</p>}
          <div className="form-actions"><button className="button button--primary" type="button" disabled={busy || result.refreshing || selectedUnavailable || selectedMailboxId === configuredMailboxId} onClick={() => void save()}>{busy ? "Saving..." : "Save portal mailbox"}</button></div>
        </>}
      </div>
    </section>
  );
}
